import { DateTime } from 'luxon';
import { ForecastParameters } from '../contexts/investment-robot/ForecastContext';
import { GoalIndicationParameters } from '../contexts/investment-robot/GoalIndicationContext';
import { NibudHouseCategory, PensionParameters } from '../contexts/pension-robot/PensionContext';
import { buildEndpoint } from './endpoints';

export type PeriodicInterval = ForecastParameters['deposits']['periodic']['interval'];

const toRequest = (params: ForecastParameters): ForecastRequest => {
    return {
        riskProfileId: Number.parseInt(params.risk.profile.id) || 8,
        paymentSingleAmount: params.deposits.initial.amount,
        paymentRecurringAmount: params.deposits.periodic.amount,
        paymentRecurringInterval: toRequestInterval(params.deposits.periodic.interval),
        investmentEndDate: yearsFromNowToDate(params.capitalGoal.inYears),
        targetCapital: params.capitalGoal.amount !== 0 ? params.capitalGoal.amount : 1,
        automaticRiskReduction: params.risk.reduction.isEnabled,
    };
};

const toRequestInterval = (periodicInterval: PeriodicInterval): ForecastRequest['paymentRecurringInterval'] => {
    switch (periodicInterval) {
        case 'monthly':
            return 'Month';

        case 'yearly':
            return 'Year';

        default:
            return 'None';
    }
};

export const yearsFromNowToDate = (numYears: number) => {
    const now = new Date();

    // We wrap the computed Date with Date.UTC so that we obtain a date in a local timezone.
    // i.e.: If its 2020-03-04T00:01 in your timezone, the date will be 2020-03-04.
    const result = new Date(Date.UTC(now.getFullYear() + numYears, now.getMonth(), now.getDate()));
    // strip time from isostring
    return result.toISOString().substring(0, 10);
};

type ForecastRequest = {
    riskProfileId: number;
    paymentSingleAmount: number;
    paymentRecurringAmount: number;
    paymentRecurringInterval: 'None' | 'Month' | 'Year';
    investmentEndDate: string;
    targetCapital?: number;
    automaticRiskReduction: boolean;
};

// type ForecastRequest = ForecastParameters;
// type ForecastResponse = Forecast;

// type Forecast = {
//     values: ForecastValue[];
//     successChance: number;
//     suggest: ForecastSuggestion;
// };

interface ForecastExpirationValues {
    date: Date;
    pessimistic: number;
    median: number;
    optimistic: number;
}


interface Suggest {
    paymentSingleAmount: number;
    paymentRecurringAmount?: number;
    paymentRecurringAmountYearly?: number;
    investmentEndDate: string;
}

interface MonthlyForecastValues {
    pessimistic: number[];
    median: number[];
    optimistic: number[];
}

export interface ForecastResponse {
    forecastExpirationValues: ForecastExpirationValues;
    successChance?: number;
    suggest?: Suggest;
    monthlyForecastValues: MonthlyForecastValues;
}

type ForecastValue = {
    date: Date;
    pessimistic: number;
    median: number;
    optimistic: number;
};

type ForecastSuggestion = {
    paymentSingleAmount: number;
    paymentRecurringAmoiunt: number;
    paymentRecurringInterval: number;
    investmentEndDate: string;
};

type RiskProfile = {
    id: number;
    label: string;
    description: string;
    funds: Fund[];
};

type Fund = {
    id: number;
    name: string;
    category: 'Stock' | 'Bond' | 'Portfolio';
    weight: number;
};

type FcRetRequest = {
    riskProfileId: number;
    paymentSingleAmount: number;
    paymentRecurringAmount: number;
    paymentRecurringInterval: 'Month' | 'Year';
    investmentEndDate: string;
    targetCapital: number;
    automaticRiskReduction: boolean;
    retirementPeriodInYears: number;
};

type FcRetResponse = {
    monthlyRetirementValues: {
        pessimistic: number;
        median: number;
        optimistic: number;
    };
    valuesOnInvestmentEndDate: {
        date: DateTime;
        pessimistic: number;
        median: number;
        optimistic: number;
    };
};

type NibudRequest = {
    hasPartner: boolean;
    pensionOfPartnerIsKnown: boolean;
    dateOfBirth: string;
    houseHoldMonthlyNetIncome: number;
    ownOrRent: NibudHouseCategory;
    isMortgageRepaid: boolean;
    mortgageAmountPerMonth: number;
    wOZValue?: string;
    monthlyRent: number;
};

export interface GetNibudResultsResponse {
    data: {
        category: string;
        value: number;
    }[];
}

type ForecastRetirementRequest = ForecastRetirement;

type ForecastRetirement = {
    investmentEndDate: string;
    targetRecurringAmount: number;
    targetRecurringInterval: 'Year' | 'Month';
    targetPeriodInYears: number;
};

const mapRetirementRequest = (req: ForecastRetirementRequest): FcRetRequest => ({
    riskProfileId: 8,
    paymentSingleAmount: 100,
    paymentRecurringAmount: 0,
    paymentRecurringInterval: 'Month',
    automaticRiskReduction: false,
    investmentEndDate: req.investmentEndDate,
    retirementPeriodInYears: req.targetPeriodInYears,
    targetCapital: 1,
});

const mapRetirementReponse = (
    response: FcRetResponse,
    originalRequest: ForecastRetirementRequest,
): { targetCapital: number } => {
    var factor = response.valuesOnInvestmentEndDate.median / response.monthlyRetirementValues.median;
    return {
        targetCapital:
            (factor * originalRequest.targetRecurringAmount) /
            (originalRequest.targetRecurringInterval === 'Year' ? 12 : 1),
    };
};

const mapNibudRequest = (params: PensionParameters): NibudRequest => ({
    hasPartner: params.personalInfo.maritalStatus !== 'single', // TODO what value
    pensionOfPartnerIsKnown: !!params.personalInfo.isCalculatedTogether,
    dateOfBirth: `${new Date().getFullYear() - (params.personalInfo.self?.age ?? 0)}-01-01`,
    houseHoldMonthlyNetIncome: (params.personalInfo.self?.salary ?? 0) + (params.personalInfo.partner?.salary ?? 0),
    ownOrRent: params.expensesInfo?.nibud?.house?.paymentCategory!,
    isMortgageRepaid: !!params.expensesInfo?.nibud?.house?.isMortgage, // TODO update the isMortgage variable, does not change at the moment
    mortgageAmountPerMonth: params.expensesInfo?.nibud?.house?.mortgage ?? 0,
    wOZValue: params.expensesInfo?.nibud?.house?.woz,
    monthlyRent: params.expensesInfo?.nibud?.house?.rent ?? 0,
});

const toRetirementTargetCapitalRequest = (params: GoalIndicationParameters): ForecastRetirementRequest => {
    const retirememtAgeInYears = params.goals?.pension?.capitalGoal?.retirememtAgeInYears || 0;
    const currentAgeInYears = params.goals?.pension?.capitalGoal?.currentAgeInYears || 0;
    const amountPerInterval = params.goals?.pension?.capitalGoal?.amountPerInterval || 0;
    const interval = params.goals?.pension?.capitalGoal?.interval || 'monthly';
    const amountIntervals = params.goals?.pension?.capitalGoal?.retirementPeriodInYears || 0;

    return {
        investmentEndDate: yearsFromNowToDate(retirememtAgeInYears - currentAgeInYears),
        targetRecurringAmount: amountPerInterval / 100,
        targetRecurringInterval: interval === 'yearly' ? 'Year' : 'Month',
        targetPeriodInYears: amountIntervals,
    };
};

//const baseUrl = 'https://devrobotapi.azurewebsites.net/roboadvisor';
//const baseUrl = 'http://localhost:3009/roboadvisor';

const forecastPath = buildEndpoint(['forecast']); //`${baseUrl}/v1/forecast`;
// const riskprofilePath = buildEndpoint(['riskprofiles'], [{ key: 'include', value: 'funds' }]); //`${baseUrl}/v1/riskprofiles?include=funds`;
const forecastRetirementPath = buildEndpoint(['forecast', 'retirement']); //`${baseUrl}/v1/forecast/retirement`;
const nibudResultsPath = buildEndpoint(['nibud']);

export const defaultHeaders = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
};

/**
 * Generate typed API fetch functions
 * @template Rs - Type of Response Body
 * @template Rq - (Optional) Type of Request Body
 *
 * @param path - The path to the resource (without leading slash)
 * @param method - The HTTP method to be used; e.g. 'GET', 'POST' etc.
 * @returns A typed api fetch function
 */
export const apifetch = <Rs, Rq = void>(path: string, method: string, headers?: { [key: string]: string }) => {
    // const url = `${baseUrl}/${path}`;
    const url = path;

    const getHeaders = () => {
        return { ...defaultHeaders, ...headers}
    }
    return async (requestBody: Rq) => {
        const request: RequestInit = {
            method,
            headers: getHeaders(),
            body: JSON.stringify(requestBody),
        };
        const response = await fetch(url, request);
        // also throw on network error, i.e. 404 not found.
        if (!response.ok) {
            throw new Error(`Network error with response ${response}`);
        }
        return response.json() as Promise<Rs>;
    };
};

// const getRiskProfiles = apifetch<RiskProfileResponse>(riskprofilePath, 'GET');
// const postForecast = apifetch<ForecastResponse, ForecastRequest>(forecastPath, 'POST');
const postForecast = async (params: ForecastParameters) =>
    await apifetch<ForecastResponse, ForecastRequest>(forecastPath, 'POST')(toRequest(params));

const postRetirementTargetCapital = async (params: GoalIndicationParameters) => {
    let request = toRetirementTargetCapitalRequest(params);
    let requestBody = mapRetirementRequest(request);
    let response = await apifetch<FcRetResponse, FcRetRequest>(forecastRetirementPath, 'POST')(requestBody);
    return mapRetirementReponse(response, request);
};

const postNibud = async (params: PensionParameters) => {
    let requestBody = mapNibudRequest(params);
    let response = await apifetch<GetNibudResultsResponse, NibudRequest>(nibudResultsPath, 'POST')(requestBody);
    return response;
};

export { postForecast, postRetirementTargetCapital, postNibud };

export type {
    ForecastRequest,
    ForecastExpirationValues,
    ForecastValue,
    ForecastSuggestion,
    RiskProfile,
    Fund,
};
